import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import CancelButton from './CancelButton';
import SaveButton from './SaveButton';
import DateInput from './DateInput';
import './StyledInput.css';
import { formatDate } from './tenantClock';
import { formatNumber } from './numberFormat';

// Sets ONE field to ONE value on every selected row (ui-architecture.md
// "Bulk Select & Bulk Edit"). Two steps in one ConfirmDialog: pick field +
// value, then a confirm that restates the change and the row count before
// anything is applied. Mounted only while open (callers render it inside
// `{open && ...}`), so its state resets on every fresh open.
//
// fields: [{ key, label, required, options: [{ value, label }] }] — an
// optional field (required: false) also offers "(none)", sent as ''. A
// field with type: 'date' takes a DateInput instead of options, sent as
// YYYY-MM-DD; its "(none)" is a checkbox that clears the date.
// onApply(fieldKey, value) returns a promise; the dialog stays open on a
// rejection so the caller's error toast can explain why.
const NONE_LABEL = '(none)';
// Value not yet picked — distinct from '' which is a real "(none)" choice.
const UNSET = '__unset';
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function BulkEditDialog({ onOpenChange, count, noun, fields, onApply }) {
  const [fieldKey, setFieldKey] = useState('');
  const [value, setValue] = useState(UNSET);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  const field = fields.find((f) => f.key === fieldKey);
  const valueChosen = !!field && value !== UNSET;
  const isDate = field?.type === 'date';
  const valueLabel = value === '' ? NONE_LABEL
    : isDate ? formatDate(value)
    : (field?.options.find((o) => String(o.value) === String(value))?.label || value);
  const countLabel = `${formatNumber(count)} ${noun}`;

  const pickField = (key) => {
    setFieldKey(key);
    setValue(UNSET);
  };

  const apply = async () => {
    setApplying(true);
    try {
      await onApply(fieldKey, value);
    } catch (err) {
      setApplying(false);
    }
  };

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!applying) onOpenChange(next); }}
      title={`Bulk Edit ${countLabel}`}
      description={confirming ? 'Review the change before applying it.' : 'Choose one field and the value to set on every selected row.'}
      footer={confirming ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={() => setConfirming(false)} disabled={applying}>Back</CancelButton>
          <SaveButton type="button" onClick={apply} loading={applying}>
            {applying ? 'Applying...' : `Apply to ${countLabel}`}
          </SaveButton>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={() => onOpenChange(false)} />
          <SaveButton type="button" onClick={() => setConfirming(true)} disabled={!valueChosen}>Next</SaveButton>
        </div>
      )}
    >
      {confirming ? (
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          Set <strong>{field.label}</strong> to <strong>{valueLabel}</strong> on <strong>{countLabel}</strong>?
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={FIELD_LABEL_STYLE}>Field</label><br />
            <select className="styled-input" value={fieldKey} onChange={(e) => pickField(e.target.value)} style={FIELD_INPUT_STYLE}>
              <option value="" disabled>Choose a field...</option>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          {field && isDate && (
            <div>
              <label style={FIELD_LABEL_STYLE}>New {field.label}</label><br />
              <DateInput
                disabled={value === ''}
                value={value === UNSET || value === '' ? '' : value}
                onChange={(e) => setValue(DATE_PATTERN.test(e.target.value) ? e.target.value : UNSET)}
                style={FIELD_INPUT_STYLE}
              />
              {!field.required && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={value === ''} onChange={(e) => setValue(e.target.checked ? '' : UNSET)} />
                  {NONE_LABEL} — clear the {field.label}
                </label>
              )}
            </div>
          )}
          {field && !isDate && (
            <div>
              <label style={FIELD_LABEL_STYLE}>New {field.label}</label><br />
              <select
                className="styled-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={FIELD_INPUT_STYLE}
              >
                <option value={UNSET} disabled>Choose...</option>
                {!field.required && <option value="">{NONE_LABEL}</option>}
                {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
    </ConfirmDialog>
  );
}

export default BulkEditDialog;
